// Sales Coach Réel — vue Manager (sessions de l'équipe).
// Accessible aux profils MNG (équipe directe) et ORG/ADM/COO (tenant entier).
import { useEffect, useState } from "react";
import { ArrowLeft, Loader2, AlertCircle, Lock, Users, TrendingUp, Activity, BarChart3, Sparkles, ShieldAlert, ChevronRight, Dumbbell } from "lucide-react";
import { salesCoachApi } from "../lib/api";
import { Badge } from "../components/ui/badge";

type Row = Awaited<ReturnType<typeof salesCoachApi.listTeamSessions>>[number];
type Stats = Awaited<ReturnType<typeof salesCoachApi.getTeamStats>>;
type CoachingCard = Awaited<ReturnType<typeof salesCoachApi.getCoachingCards>>[number];

const SIMULATOR_LABELS: Record<string, string> = {
  PROSPECTOR: "Prospector", DISCOVERY: "Discovery", PITCH: "Pitch",
  NEGOTIATOR: "Negotiator", SATISFACTOR: "Satisfactor", MEDDIC: "MEDDIC",
};

interface Props {
  onBack: () => void;
  onOpenSession: (id: string) => void;
}

const TIER_COLORS: Record<string, { bg: string; text: string }> = {
  excellent: { bg: "#7CBF94", text: "#1E7A3C" },
  good: { bg: "#A6D3F9", text: "#384872" },
  needs_improvement: { bg: "#F6C847", text: "#926B00" },
  insufficient: { bg: "#F49390", text: "#C0423F" },
};

const CONTEXT_LABELS: Record<string, string> = {
  cold_call: "Cold call", discovery: "Découverte", pitch: "Pitch",
  negotiation: "Négo", customer_success: "SAV", qualification: "Qualif", generic: "Générique",
};

const FILTER_OPTIONS = [
  { value: "", label: "Tous les scores" },
  { value: "80", label: "Excellent (≥80)" },
  { value: "60", label: "Bon (≥60)" },
  { value: "40", label: "À améliorer (≥40)" },
];

export function SalesCoachTeamPage({ onBack, onOpenSession }: Props) {
  const [sessions, setSessions] = useState<Row[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  // V2 chantier 5 — coaching cards (brief 1:1 par commercial)
  const [coachingCards, setCoachingCards] = useState<CoachingCard[]>([]);
  const [filterUserId, setFilterUserId] = useState<string>("");
  const [filterMinScore, setFilterMinScore] = useState<string>("");
  const [filterContext, setFilterContext] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      salesCoachApi.listTeamSessions({
        userId: filterUserId || undefined,
        minScore: filterMinScore ? Number(filterMinScore) : undefined,
        contextType: filterContext || undefined,
        limit: 100,
      }),
      salesCoachApi.getTeamStats(),
    ])
      .then(([s, st]) => { setSessions(s); setStats(st); setError(null); })
      .catch((err) => setError(err instanceof Error ? err.message : "Erreur"))
      .finally(() => setLoading(false));
  }, [filterUserId, filterMinScore, filterContext]);

  // Coaching cards : chargées une fois au mount (indépendant des filtres tableau)
  useEffect(() => {
    salesCoachApi.getCoachingCards().then(setCoachingCards).catch(() => setCoachingCards([]));
  }, []);

  // Liste unique des commerciaux pour le filtre
  const commercials = Array.from(new Map(sessions.map((s) => [s.userId, `${s.userFirstName ?? ""} ${s.userLastName ?? ""}`.trim()])).entries());

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <button onClick={onBack} className="flex items-center gap-2 text-sm hover:underline" style={{ color: "#384872" }}>
        <ArrowLeft className="h-4 w-4" /> Retour
      </button>

      <div className="flex items-start gap-3 flex-wrap">
        <div className="h-12 w-12 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: "#988EF2" }}>
          <Users className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
            Sessions de mon équipe
          </h1>
          <p className="text-sm text-gray-500">Suivi des analyses Sales Coach Réel des commerciaux que vous managez</p>
        </div>
      </div>

      {/* KPI cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <KpiTile icon={<BarChart3 className="h-3.5 w-3.5" />} label="Total sessions" value={stats.totalSessions} />
          <KpiTile icon={<TrendingUp className="h-3.5 w-3.5" />} label="Score moyen" value={stats.averageScore !== null ? `${stats.averageScore}/100` : "—"} />
          <KpiTile icon={<Activity className="h-3.5 w-3.5" />} label="Commerciaux actifs" value={stats.activeCommercials} />
        </div>
      )}

      {/* V2 chantier 5 — Coaching cards : brief 1:1 par commercial */}
      {coachingCards.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-5 w-5" style={{ color: "#5A4BBE" }} />
            <h2 className="text-lg font-semibold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
              Brief pour vos 1:1 (30 derniers jours)
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {coachingCards.map((c) => (
              <div key={c.userId} className="rounded-xl border bg-white p-4 space-y-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <div className="font-semibold" style={{ color: "#384872" }}>{c.userName}</div>
                    <div className="text-xs" style={{ color: "#6B7280" }}>
                      {c.sessionsCount} session{c.sessionsCount > 1 ? "s" : ""} ·{" "}
                      {c.avgScore != null ? `score moyen ${c.avgScore}/100` : "—"}
                    </div>
                  </div>
                  {c.poorlyHandledObjectionsCount > 0 && (
                    <Badge style={{ background: "#FFE0DE", color: "#C0423F", fontSize: "11px" }}>
                      <ShieldAlert className="h-3 w-3 inline mr-1" />
                      {c.poorlyHandledObjectionsCount} objection{c.poorlyHandledObjectionsCount > 1 ? "s" : ""} à traiter
                    </Badge>
                  )}
                </div>

                {c.callsToReview.length > 0 && (
                  <div>
                    <div className="text-xs uppercase tracking-wider font-semibold mb-1.5" style={{ color: "#6B7280" }}>
                      Appels à revoir ensemble
                    </div>
                    <div className="space-y-1.5">
                      {c.callsToReview.map((call) => (
                        <button
                          key={call.sessionId}
                          onClick={() => onOpenSession(call.sessionId)}
                          className="w-full text-left rounded-lg p-2 hover:bg-gray-50 transition-colors flex items-start gap-2"
                          style={{ background: "#F9FAFB" }}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate" style={{ color: "#384872" }}>
                              {call.prospectName || "Anonyme"}
                              {call.prospectCompany && <span className="text-xs ml-1" style={{ color: "#6B7280" }}>· {call.prospectCompany}</span>}
                            </div>
                            {call.topImprovementTitle && (
                              <div className="text-xs truncate" style={{ color: "#C0423F" }}>
                                ↓ {call.topImprovementTitle}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {call.scoreGlobal != null && (
                              <Badge style={{
                                background: call.scoreGlobal >= 60 ? "#DDEEFF" : "#FFE0DE",
                                color: call.scoreGlobal >= 60 ? "#384872" : "#C0423F",
                                fontSize: "10px",
                              }}>
                                {call.scoreGlobal}
                              </Badge>
                            )}
                            <ChevronRight className="h-3.5 w-3.5" style={{ color: "#9CA3AF" }} />
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {c.topRecommendedSimulator && (
                  <div className="rounded-lg p-2.5 flex items-center gap-2" style={{ background: "#F3F0FF" }}>
                    <Dumbbell className="h-4 w-4 shrink-0" style={{ color: "#5A4BBE" }} />
                    <div className="text-xs flex-1" style={{ color: "#384872" }}>
                      <span className="font-semibold">Simulation à pousser :</span>{" "}
                      {SIMULATOR_LABELS[c.topRecommendedSimulator.key] ?? c.topRecommendedSimulator.key}
                      <span style={{ color: "#6B7280" }}> (recommandée {c.topRecommendedSimulator.count}×)</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="flex flex-wrap gap-2">
        <select value={filterUserId} onChange={(e) => setFilterUserId(e.target.value)} className="rounded-md border bg-white px-3 py-2 text-sm" style={{ minHeight: 40 }}>
          <option value="">Tous les commerciaux</option>
          {commercials.map(([id, name]) => (
            <option key={id} value={id}>{name || id.slice(0, 8)}</option>
          ))}
        </select>
        <select value={filterMinScore} onChange={(e) => setFilterMinScore(e.target.value)} className="rounded-md border bg-white px-3 py-2 text-sm" style={{ minHeight: 40 }}>
          {FILTER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={filterContext} onChange={(e) => setFilterContext(e.target.value)} className="rounded-md border bg-white px-3 py-2 text-sm" style={{ minHeight: 40 }}>
          <option value="">Tous les contextes</option>
          {Object.entries(CONTEXT_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>

      {loading && (
        <div className="text-center py-12 text-gray-500">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3" /> Chargement…
        </div>
      )}

      {error && (
        <div className="rounded-xl border-2 p-4 flex items-start gap-3" style={{ borderColor: "#F49390", background: "#FFF5F5" }}>
          <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" style={{ color: "#C0423F" }} />
          <div className="text-sm" style={{ color: "#C0423F" }}>{error}</div>
        </div>
      )}

      {!loading && !error && sessions.length === 0 && (
        <div className="rounded-xl border-2 border-dashed p-12 text-center text-gray-500">
          Aucune session trouvée avec ces filtres.
        </div>
      )}

      {!loading && sessions.length > 0 && (
        <div className="rounded-xl border bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Commercial</th>
                  <th className="px-4 py-3 text-left font-semibold">Prospect</th>
                  <th className="px-4 py-3 text-left font-semibold">Contexte</th>
                  <th className="px-4 py-3 text-left font-semibold">Score</th>
                  <th className="px-4 py-3 text-left font-semibold">Date</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.id} className="border-t hover:bg-gray-50 cursor-pointer" onClick={() => onOpenSession(s.id)}>
                    <td className="px-4 py-3 font-medium" style={{ color: "#384872" }}>
                      {[s.userFirstName, s.userLastName].filter(Boolean).join(" ") || "—"}
                      {s.isPrivate && <Lock className="inline h-3 w-3 ml-1.5 text-gray-400" />}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {s.contextProspectName ?? "—"}
                      {s.contextProspectCompany && <span className="text-gray-400 text-xs ml-1">· {s.contextProspectCompany}</span>}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline">{CONTEXT_LABELS[s.contextType] ?? s.contextType}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      {s.analysisStatus === "completed" && s.scoreGlobal !== null && s.scoreTier && (
                        <Badge style={{ background: TIER_COLORS[s.scoreTier]?.bg, color: TIER_COLORS[s.scoreTier]?.text }}>
                          {s.scoreGlobal}/100
                        </Badge>
                      )}
                      {s.analysisStatus === "in_progress" && (
                        <span className="text-xs text-gray-400 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> En cours</span>
                      )}
                      {s.analysisStatus === "failed" && (
                        <span className="text-xs" style={{ color: "#C0423F" }}>Échec</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{formatDate(s.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiTile({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-xl border bg-white p-4">
      <div className="text-xs text-gray-500 flex items-center gap-1.5 mb-1.5">{icon}<span>{label}</span></div>
      <div className="text-2xl font-bold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>{value}</div>
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" });
  } catch { return iso; }
}
